import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Stack,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { CheckCircle, Add, PersonOutlined, LocationOnOutlined, AlternateEmailOutlined, VerifiedUserOutlined, LocalPhoneOutlined } from '@mui/icons-material';
import http from 'utils/http';
import cookies from 'utils/cookies';
import { countryCode, states_city, DeleteIcon } from 'custom/constants';
import { StyledWhiteCard, CustomButton, SelectInputProps, CustomInputLabel, CustomTextField, InputProps, CustomSelect } from 'custom/components';
import CABALogo from '../../assets/images/orgaLogo.svg';
import consigneeStyles from './consigneereg.module.css';

class ConsigneeRegistrationForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: '',
      success: '',
      loading: true,
      consigneeData: null,
      formData: {
        name: '',
        contactName: '',
        email: '',
        phone_code: '',
        phone: '',
        address: '',
        address2: '',
        state: '',
        city: '',
        zip: '',
      },
      otp: '',
      otpSent: false,
      otpVerified: false,
      otpError: '',
      resendTimer: 0,
      submitting: false,
      mails: [],
      emailErrors: {},
      errors: {},
    };
    this.resendInterval = null;
  }

  componentDidMount() {
    this.validateToken();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location?.search !== this.props.location?.search) {
      this.setState(
        {
          loading: true,
          error: '',
          success: '',
          consigneeData: null,
        },
        this.validateToken
      );
    }

    if (prevState.resendTimer !== this.state.resendTimer) {
      if (this.state.resendTimer > 0 && !this.resendInterval) {
        this.resendInterval = setInterval(() => {
          this.setState((state) => ({ resendTimer: state.resendTimer > 0 ? state.resendTimer - 1 : 0, }));
        }, 1000);
      }

      if (this.state.resendTimer <= 0 && this.resendInterval) {
        clearInterval(this.resendInterval);
        this.resendInterval = null;
      }
    }

    if (
      prevState.formData.email !== this.state.formData.email ||
      prevState.mails !== this.state.mails
    ) {
      this.revalidateEmails();
    }
  }

  componentWillUnmount() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
  }

  getToken = () => {
    const searchParams = new URLSearchParams(this.props.location?.search || '');
    return searchParams.get('token');
  };

  validateToken = async () => {
    const token = this.getToken();
    console.log('[CONSIGNEE-REG] Token from URL:', token);

    if (!token) {
      this.setState({
        error: 'Invalid registration link. Token is missing.',
        loading: false,
      });
      return;
    }

    try {
      console.log('[CONSIGNEE-REG] Validating token...');
      const response = await http.get(`consignee/registration/validate?token=${token}`, { headers: { hideloader: true } });
      console.log('[CONSIGNEE-REG] Validation response:', response);

      if (response.data.status === 'success') {
        const data = response.data.data || {};
        const notificationEmails = Array.isArray(data.mails)
          ? data.mails.filter((mail) => !mail.deleted)
          : [];

        this.setState((state) => ({
          consigneeData: data,
          formData: {
            ...state.formData,
            ...(data.name ? { name: data.name } : {}),
            ...(data.contactName ? { contactName: data.contactName } : {}),
            ...(data.email ? { email: data.email } : {}),
            ...(data.phone_code ? { phone_code: data.phone_code } : {}),
            ...(data.phone ? { phone: data.phone } : {}),
            ...(data.address ? { address: data.address } : {}),
            ...(data.address2 ? { address2: data.address2 } : {}),
            ...(data.city ? { city: data.city } : {}),
            ...(data.state ? { state: data.state } : {}),
            ...(data.zip ? { zip: data.zip } : {}),
          },
          mails: notificationEmails,
        }));
      } else {
        this.setState({ error: response.data.message || 'Invalid or expired registration link.', });
      }
    } catch (err) {
      console.error('[CONSIGNEE-REG] Token validation error:', err);
      console.error('[CONSIGNEE-REG] Error response:', err?.response);
      const errorMessage = err?.response?.data?.message || 'This registration link is invalid or has expired. Please contact your company should you need to update your registration again.';
      this.setState({ error: errorMessage });
    } finally {
      this.setState({ loading: false });
    }
  };

  revalidateEmails = () => {
    const { formData, mails } = this.state;
    if (!formData.email || mails.length === 0) {
      if (Object.keys(this.state.emailErrors).length > 0) {
        this.setState({ emailErrors: {} });
      }
      return;
    }

    const newErrors = {};
    mails.forEach((mail, index) => {
      if (!mail.deleted && mail.email) {
        const allEmails = [
          formData.email.toLowerCase(),
          ...mails
            .filter((m, i) => !m.deleted && i !== index)
            .map((m) => m.email.toLowerCase()),
        ];

        if (allEmails.includes(mail.email.toLowerCase())) {
          newErrors[index] = 'This email address already exists';
        }
      }
    });

    this.setState({ emailErrors: newErrors });
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Company / Consignee Name is required';
    }
    if (!formData.contactName || formData.contactName.trim() === '') {
      errors.contactName = 'Consignee Contact Name is required';
    }
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (!formData.address || formData.address.trim() === '') {
      errors.address = 'Address 1 is required';
    }
    if (!formData.address2 || formData.address2.trim() === '') {
      errors.address2 = 'Address 2 is required';
    }
    if (!formData.state || formData.state.trim() === '') {
      errors.state = 'State is required';
    }
    if (!formData.city || formData.city.trim() === '') {
      errors.city = 'City is required';
    }
    if (!formData.zip || formData.zip.trim() === '') {
      errors.zip = 'Zip Code is required';
    }
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((state) => {
      const updatedFormData = {
        ...state.formData,
        [name]: value,
      };
      
      const updatedErrors = { ...state.errors };
      if (updatedErrors[name]) {
        delete updatedErrors[name];
      }
      
      return {
        formData: updatedFormData,
        errors: updatedErrors,
      };
    });
  };

  handleAddEmail = () => {
    this.setState((state) => ({
      mails: [
        ...state.mails,
        {
          email: '',
          deleted: false,
        },
      ],
    }));
  };

  handleChangeEmail = (index, e) => {
    const newEmail = e.target.value;
    const { formData } = this.state;

    this.setState((state) => {
      const updatedMails = [...state.mails];
      updatedMails[index] = {
        ...updatedMails[index],
        [e.target.name]: newEmail,
      };

      const updatedErrors = { ...state.emailErrors };

      if (newEmail) {
        const allEmails = [
          formData.email.toLowerCase(),
          ...updatedMails
            .filter((m, i) => !m.deleted && i !== index)
            .map((m) => m.email.toLowerCase()),
        ];

        if (allEmails.includes(newEmail.toLowerCase())) {
          updatedErrors[index] = 'This email address already exists';
        } else {
          delete updatedErrors[index];
        }
      } else {
        delete updatedErrors[index];
      }

      return {
        mails: updatedMails,
        emailErrors: updatedErrors,
      };
    });
  };

  handleDeleteEmail = (index) => {
    this.setState((state) => {
      const updatedMails = [...state.mails];
      updatedMails[index] = {
        ...updatedMails[index],
        deleted: true,
      };

      const updatedErrors = { ...state.emailErrors };
      delete updatedErrors[index];

      return {
        mails: updatedMails,
        emailErrors: updatedErrors,
      };
    });
  };

  handleSendOtp = async () => {
    const { formData: { email }, } = this.state;
    const token = this.getToken();

    if (!email) {
      this.setState({
        otpError: 'Please enter an email address',
        otpSent: false,
      });
      return;
    }

    try {
      this.setState({
        otpError: '',
        resendTimer: 15,
      });

      const response = await http.post(
        'consignee/registration/send-otp',
        {
          email,
          token,
        },
        { headers: { hideloader: true } }
      );

      if (response.data.status === 'success') {
        this.setState({
          otpSent: true,
          otpError: '',
        });
      } else {
        this.setState({ otpError: response.data.message || 'Failed to send OTP', });
      }
    } catch (err) {
      this.setState({ otpError: 'Failed to send OTP to your email. Please try again.', });
    }
  };

  handleVerifyOtp = async () => {
    const {
      otp,
      formData: { email },
    } = this.state;
    const token = this.getToken();

    if (!otp) {
      this.setState({ otpError: 'Please enter the OTP' });
      return;
    }

    try {
      this.setState({ otpError: '' });
      const response = await http.post(
        'consignee/registration/verify-otp',
        {
          email,
          otp,
          token
        },
        { headers: { hideloader: true } }
      );

      if (response.data.status === 'success') {
        this.setState({
          otpVerified: true,
          otpError: '',
        });
      } else {
        this.setState({ otpError: response.data.message || 'Invalid OTP', });
      }
    } catch (err) {
      this.setState({ otpError: 'Failed to verify OTP. Please try again.', });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const {
      otpSent,
      otpVerified,
      emailErrors,
      formData,
      otp,
      consigneeData,
      mails,
    } = this.state;
    const token = this.getToken();

    if (!this.validateForm()) {
      this.setState({ error: 'Please fill in all required fields' });
      return;
    }

    if (!otpSent) {
      this.setState({ otpSent: true });
      await this.handleSendOtp();
      return;
    }

    if (!otpVerified) {
      this.setState({ error: 'Please verify the code sent to your email first' });
      return;
    }

    if (Object.keys(emailErrors).length > 0) {
      this.setState({ error: 'Please fix email validation errors before submitting' });
      return;
    }

    try {
      this.setState({
        submitting: true,
        error: '',
      });

      const fullPhone = formData.phone ? `${formData.phone_code || ''}${formData.phone}` : '';
      const response = await http.post(
        'consignee/registration/submit',
        {
          name: formData.name,
          contactName: formData.contactName,
          email: formData.email,
          phone: fullPhone,
          address: formData.address,
          address2: formData.address2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          otp,
          company: consigneeData?.companyId,
          station: consigneeData?.stationId,
          token,
          mails: mails.filter((mail) => !mail.deleted && mail.email),
        },
        { headers: { hidloader: true } }
      );

      if (response.data.status === 'success') {
        this.setState({ success: 'Thank you! Your information has been registered successfully.', });
      } else {
        this.setState({ error: response.data.message || 'Failed to submit registration', });
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Failed to submit registration. Please try again.';
      this.setState({ error: errorMessage });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const {
      error,
      success,
      loading,
      consigneeData,
      formData,
      otp,
      otpSent,
      otpVerified,
      otpError,
      resendTimer,
      submitting,
      mails,
      emailErrors,
      errors,
    } = this.state;

    if (loading) {
      return (
        <Box className={consigneeStyles.screenCenter}>
          <CircularProgress />
        </Box>
      );
    }

    if (error && !consigneeData) {
      return (
        <Box className={consigneeStyles.screenCenter}>
          <Container maxWidth='sm'>
            <StyledWhiteCard className={consigneeStyles.statusCard}>
              <Alert severity='error' className={consigneeStyles.centeredAlert}>{error}</Alert>
            </StyledWhiteCard>
          </Container>
        </Box>
      );
    }

    if (success) {
      return (
        <Box className={consigneeStyles.screenCenter}>
          <Container maxWidth='sm'>
            <StyledWhiteCard className={consigneeStyles.statusCardLarge}>
              <CheckCircle color='success' className={consigneeStyles.successIcon} />
              <Typography variant='h5' color='text.primary' fontWeight={600} gutterBottom>
                Registration Successful!
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                {success}
              </Typography>
            </StyledWhiteCard>
          </Container>
        </Box>
      );
    }

    return (
      <Box className={consigneeStyles.pageRoot}>
        {/* Sleek App Header Mimicking Main App Header */}
        <Box className={consigneeStyles.headerSpacer}>
        
        </Box>

        <Container maxWidth='md'>
          <StyledWhiteCard className={consigneeStyles.formCard}>

            <Stack direction='row' justifyContent='space-between' alignItems='center' className={consigneeStyles.heroRow}>
              <Box className={consigneeStyles.logoBlock}>
                <Box component='img' src={CABALogo} alt='CABALogo' width={90} sx={{}} />
              </Box>
              <Stack direction='column' justifyContent='center' className={consigneeStyles.heroTitleWrap}>
                <Typography variant='body2' color='primary' fontWeight='600' gutterBottom textAlign='right' pb='12px'>
                  {`${consigneeData?.company_name || cookies.get('company_name') || ''} | ${consigneeData?.station_code || cookies.get('station_code') || ''}`}
                </Typography>
                <Typography variant='h5' color='primary' fontWeight='700' pb='4px' fontSize='1.5rem' textTransform='uppercase'>
              Update Your Contact Information
                </Typography>
                <Typography variant='body2' color='text.secondary' className={consigneeStyles.introText}>
              Please provide or confirm your contact details to receive timely arrival notifications for your airfreight shipments.
                </Typography>
              </Stack>
            </Stack>

            {error && (
              <Alert severity='error' className={consigneeStyles.formErrorAlert}>
                {error}
              </Alert>
            )}

            <form onSubmit={this.handleSubmit} >
              <Box className={consigneeStyles.formFields}>
                <Grid container spacing={5}>
                  {/* Account Details Section */}
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <PersonOutlined color='primary' />
                          <Typography variant='subtitle2' color='primary' className={`${consigneeStyles.sectionTitle} ${consigneeStyles.sectionTitleBorder}`}>
                     Account Details
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <CustomInputLabel required htmlFor='name'>Company / Consignee Name</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='name'
                          fullWidth
                          placeholder='Company / Consignee Name'
                          name='name'
                          value={formData.name}
                          onChange={this.handleChange}
                          error={!!errors.name}
                          helperText={errors.name}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <CustomInputLabel required htmlFor='contactName'>Consignee Contact Name</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='contactName'
                          fullWidth
                          placeholder='Consignee Contact Name'
                          name='contactName'
                          value={formData.contactName || ''}
                          onChange={this.handleChange}
                          error={!!errors.contactName}
                          helperText={errors.contactName}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <CustomInputLabel required htmlFor='email'>Email</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='email'
                          fullWidth
                          placeholder='Email'
                          type='email'
                          name='email'
                          value={formData.email}
                          onChange={this.handleChange}
                          error={!!errors.email}
                          helperText={errors.email}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Physical Address Section */}
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <LocationOnOutlined color='primary' />
                          <Typography variant='subtitle2' color='primary' className={`${consigneeStyles.sectionTitle} ${consigneeStyles.sectionTitleBorder}`}>
                  Physical Address
                          </Typography>
                        </Stack>

                      </Grid>

                      <Grid item xs={12} md={12}>
                        <CustomInputLabel required htmlFor='address'>Address 1</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='address'
                          fullWidth
                          placeholder='Address 1'
                          name='address'
                          value={formData.address}
                          onChange={this.handleChange}
                          error={!!errors.address}
                          helperText={errors.address}
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <CustomInputLabel required htmlFor='address2'>Address 2</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='address2'
                          fullWidth
                          placeholder='Address 2'
                          name='address2'
                          value={formData.address2}
                          onChange={this.handleChange}
                          error={!!errors.address2}
                          helperText={errors.address2}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomInputLabel required htmlFor='state'>State</CustomInputLabel>
                        <FormControl error={!!errors.state} fullWidth>
                          <CustomSelect
                            {...SelectInputProps}
                            id='state'
                            fullWidth
                            name='state'
                            value={formData.state}
                            onChange={this.handleChange}
                            displayEmpty
                            error={!!errors.state}
                          >
                            <MenuItem value='' disabled>Select State</MenuItem>
                            {Object.keys(states_city).sort((a, b) => a.localeCompare(b)).map((state) => (
                              <MenuItem key={state} value={state}>
                                {state}
                              </MenuItem>
                            ))}
                          </CustomSelect>
                          {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomInputLabel required htmlFor='city'>City</CustomInputLabel>
                        <FormControl error={!!errors.city} fullWidth>
                          <CustomSelect
                            {...SelectInputProps}
                            id='city'
                            fullWidth
                            name='city'
                            value={formData.city}
                            onChange={this.handleChange}
                            disabled={!formData.state}
                            displayEmpty
                            error={!!errors.city}
                          >
                            <MenuItem value='' disabled>Select City</MenuItem>
                            {formData.state && states_city[formData.state] && 
                      states_city[formData.state].cities.sort((a, b) => a.localeCompare(b)).map((city) => (
                        <MenuItem key={city} value={city}>
                          {city}
                        </MenuItem>
                      ))
                            }
                          </CustomSelect>
                          {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomInputLabel required htmlFor='zip'>Zip Code</CustomInputLabel>
                        <CustomTextField
                          {...InputProps}
                          id='zip'
                          fullWidth
                          placeholder='Zip Code'
                          name='zip'
                          value={formData.zip}
                          onChange={this.handleChange}
                          error={!!errors.zip}
                          helperText={errors.zip}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Notification Email List Section */}
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between' className={consigneeStyles.notificationHeaderRow}>
                          <Stack direction='row' spacing={1} alignItems='center' flex='auto'>
                            <AlternateEmailOutlined color='primary' />
                            <Stack direction='column' spacing={0} flex='auto' className={consigneeStyles.sectionTitleBorder}>
                              <Typography variant='subtitle2' color='primary' className={consigneeStyles.sectionTitle}>
                      Notification Email List
                              </Typography>
                              <Typography variant='caption' color='text.secondary' className={consigneeStyles.notificationCaption}>
                    Add additional email addresses to receive arrival notifications
                              </Typography>
                            </Stack>
                          </Stack>
                   
                          <CustomButton
                            variant='outlined'
                            size='small'
                            onClick={this.handleAddEmail}
                            startIcon={<Add />}
                            className={consigneeStyles.addEmailButton}
                          >
                      Add Email
                          </CustomButton>
                        </Stack>
                      </Grid>

                      {/* Primary Email (disabled) */}
                      <Grid item xs={12}>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={11}>
                            <CustomTextField
                              {...InputProps}
                              fullWidth
                              placeholder='E-mail'
                              value={formData.email}
                              disabled
                              aria-label='Primary email'
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <DeleteIcon disabled className={consigneeStyles.delIcon} />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Additional Email Fields */}
                      {mails.map((mail, index) => (
                        !mail.deleted && (
                          <Grid item xs={12} key={index}>
                            <Grid container spacing={2} alignItems='center'>
                              <Grid item xs={11}>
                                <CustomTextField
                                  {...InputProps}
                                  fullWidth
                                  placeholder='E-mail'
                                  type='email'
                                  name='email'
                                  value={mail.email}
                                  onChange={(e) => this.handleChangeEmail(index, e)}
                                  autoFocus
                                  error={!!emailErrors[index]}
                                  helperText={emailErrors[index] || ''}
                                  aria-label={`Additional email ${index + 1}`}
                                />
                              </Grid>
                              <Grid item xs={1}>
                                <DeleteIcon onClick={() => this.handleDeleteEmail(index)} className={consigneeStyles.delIcon} />
                              </Grid>
                            </Grid>
                          </Grid>
                        )
                      ))}

                    </Grid>
                  </Grid>

                  {/* Phone Section */}
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <LocalPhoneOutlined color='primary' />
                          <Typography variant='subtitle2' color='primary' className={`${consigneeStyles.sectionTitle} ${consigneeStyles.sectionTitleBorder}`}>
                     Phone Details
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomInputLabel>Phone Number</CustomInputLabel>
                        <Stack direction={{
                          xs: 'column',
                          sm: 'row' 
                        }} spacing={2} alignItems={{
                          xs: 'stretch',
                          sm: 'flex-start' 
                        }}>
                          <Box className={consigneeStyles.phoneCodeWrapper}>
                            <CustomSelect
                              {...SelectInputProps}
                              fullWidth
                              name='phone_code'
                              value={formData.phone_code}
                              onChange={this.handleChange}
                              renderValue={(selected) => selected}
                            >
                              {countryCode.map((code) => (
                                <MenuItem key={code.dial_code} value={code.dial_code}>
                                  {code.name} ({code.dial_code})
                                </MenuItem>
                              ))}
                            </CustomSelect>
                          </Box>
                          <CustomTextField
                            {...InputProps}
                            fullWidth
                            placeholder='Enter 10-digit phone number'
                            name='phone'
                            value={formData.phone}
                            onChange={this.handleChange}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* OTP Input - shown after clicking Verify Email */}
                  {otpSent && !otpVerified && (
                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <VerifiedUserOutlined color='primary' />
                            <Typography variant='subtitle2' color='primary' className={`${consigneeStyles.sectionTitle} ${consigneeStyles.sectionTitleBorder}`}>
                        Verification
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Box className={consigneeStyles.otpBox}>
                            <Typography variant='body2' color='primary' className={consigneeStyles.otpCaption}>
                        Enter verification code sent to {formData.email}
                            </Typography>
                            <Stack direction='row' spacing={1} alignItems='flex-end' >
                              <div className={consigneeStyles.otpInputWrap}>
                                <CustomInputLabel htmlFor='otp'>Verification code</CustomInputLabel>
                                <CustomTextField
                                  {...InputProps}
                                  fullWidth
                                  id='otp'
                                  value={otp}
                                  onChange={(e) => this.setState({ otp: e.target.value })}
                                  placeholder='Enter 6-digit code'
                                  inputProps={{ maxLength: 6 }}
                                  error={!!otpError}
                                  helperText={otpError}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && otp && otp.length === 6) {
                                      this.handleVerifyOtp();
                                    }
                                  }}
                                />
                              </div>
                              <CustomButton
                                variant='contained'
                                onClick={this.handleVerifyOtp}
                                disabled={!otp || otp.length !== 6}
                                className={consigneeStyles.verifyButton}
                              >
                          Verify
                              </CustomButton>
                              <CustomButton
                                variant='outlined'
                                onClick={this.handleSendOtp}
                                disabled={resendTimer > 0}
                                className={consigneeStyles.resendButton}
                              >
                                {resendTimer > 0 ? `Resend (${resendTimer}s)` : 'Resend'}
                              </CustomButton>
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  {/* Email Verified Message */}
                  {otpVerified && (
                    <Grid item xs={12}>
                      <Box className={consigneeStyles.verifiedMessage}>
                        <CheckCircle color='success' />
                        <Typography variant='body2' color='success.main' fontWeight={600}>
                        Email verified successfully
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Submit / Verify Button */}
                  <Grid item xs={12}>
                    <Box className={consigneeStyles.submitWrap}>
                      <CustomButton
                        type='submit'
                        variant='contained'
                        size='large'
                        disabled={submitting || (otpSent && !otpVerified)}
                        className={consigneeStyles.submitButton}
                      >
                        {submitting ? (
                          <CircularProgress size={24} color='inherit' />
                        ) : otpVerified ? (
                          'Complete Registration'
                        ) : (
                          'Verify Email'
                        )}
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </form>
          </StyledWhiteCard>
        </Container>
      </Box>
    );
  }
}

const ConsigneeRegistrationFormWithLocation = (props) => {
  const location = useLocation();
  return <ConsigneeRegistrationForm {...props} location={location} />;
};

export default ConsigneeRegistrationFormWithLocation;
